import React from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { ClipboardList, Lightbulb, HeartHandshake } from 'lucide-react';

const pillars = [
  {
    icon: <ClipboardList size={28} strokeWidth={1.5} />,
    tag: 'Step One',
    title: 'Assess',
    desc: 'Structured, research-validated career and psychological assessments that explore aptitude mapping, personality, learning style, multiple intelligence, emotional intelligence, and cognitive strengths.',
    color: '#2A9D8F',
    bg: 'rgba(42,157,143,0.08)',
    border: 'rgba(42,157,143,0.16)',
  },
  {
    icon: <Lightbulb size={28} strokeWidth={1.5} />,
    tag: 'Step Two',
    title: 'Interpret',
    desc: 'AI-powered advisory reports translate assessment data into stream selection, subject selection, career roadmaps, and higher education guidance. No jargon. Just clarity.',
    color: '#F4A261',
    bg: 'rgba(244,162,97,0.08)',
    border: 'rgba(244,162,97,0.2)',
  },
  {
    icon: <HeartHandshake size={28} strokeWidth={1.5} />,
    tag: 'Step Three',
    title: 'Guide',
    desc: 'Expert counselors provide personalized sessions, next-step plans, college guidance, and future-skills direction. Human understanding meets structured guidance.',
    color: '#8DA399',
    bg: 'rgba(141,163,153,0.1)',
    border: 'rgba(141,163,153,0.22)',
  },
];

const WhatWeDo = () => {
  const [ref, visible] = useScrollReveal();

  return (
    <section
      id="about"
      ref={ref}
      style={{ padding: '100px 24px', background: '#F9F9F7' }}
      data-testid="what-we-do-section"
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div className={`reveal ${visible ? 'visible' : ''}`} style={{ textAlign: 'center', marginBottom: '64px' }}>
          <div className="section-label">What We Do</div>
          <div className="section-divider" />
          <h2 style={{
            fontFamily: 'Manrope', fontWeight: 300, fontSize: 'clamp(32px,4vw,48px)',
            color: '#1A202C', margin: '20px 0 16px', letterSpacing: '-0.02em',
          }}>
            One Integrated Platform.<br />
            <span style={{ fontWeight: 700, color: '#2A9D8F' }}>Three Powerful Layers.</span>
          </h2>
          <p style={{ fontSize: '16px', color: '#64748B', maxWidth: '500px', margin: '0 auto', lineHeight: 1.7 }}>
            MindVrik works through a seamless three-step journey — from assessment to insight to guided action.
          </p>
        </div>

        {/* Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
          {pillars.map((p, i) => (
            <div
              key={p.title}
              className={`card-lift reveal reveal-delay-${i + 2} ${visible ? 'visible' : ''}`}
              data-testid={`pillar-card-${i}`}
              style={{
                background: 'rgba(255,255,255,0.78)',
                backdropFilter: 'blur(16px)',
                border: `1px solid ${p.border}`,
                borderRadius: '24px',
                padding: '40px 36px',
                boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {/* Subtle background glow */}
              <div style={{
                position: 'absolute', top: '-40px', right: '-40px',
                width: '180px', height: '180px',
                background: `radial-gradient(circle, ${p.bg.replace('0.08', '0.12')} 0%, transparent 70%)`,
                borderRadius: '50%', pointerEvents: 'none',
              }} />
              {/* Icon */}
              <div style={{
                width: '64px', height: '64px', borderRadius: '18px',
                background: p.bg, border: `1px solid ${p.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: p.color, marginBottom: '24px',
              }}>{p.icon}</div>
              {/* Tag */}
              <div style={{ fontSize: '11px', fontFamily: 'Manrope', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: p.color, marginBottom: '8px' }}>{p.tag}</div>
              {/* Title */}
              <h3 style={{ fontFamily: 'Manrope', fontWeight: 700, fontSize: '28px', color: '#1A202C', margin: '0 0 16px', letterSpacing: '-0.01em' }}>{p.title}</h3>
              {/* Desc */}
              <p style={{ fontFamily: 'Inter', fontSize: '15px', color: '#64748B', lineHeight: 1.72, margin: 0 }}>{p.desc}</p>

              {/* Bottom accent line */}
              <div style={{ height: '3px', background: `linear-gradient(90deg, ${p.color}, ${p.color}22)`, borderRadius: '99px', marginTop: '32px' }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhatWeDo;
